// ==================== COMPOSANT CALENDRIER VISUEL CONGÉS (PHASE 3) ====================
import { API_BASE } from '../../config';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../../i18n/LanguageContext';

const LeaveCalendar = ({ user }) => {
  const { t, language } = useTranslation();
  const dateLocale = language === 'en' ? 'en-US' : 'fr-FR';
  const [currentDate, setCurrentDate] = useState(new Date());
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // month | annual
  const [annualYear, setAnnualYear] = useState(() => new Date().getFullYear());
  const [showExportButtons, setShowExportButtons] = useState(false); // true after "Generate annual calendar" is clicked
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

  // Approved leaves only (for annual view and exports)
  const approvedLeaves = useMemo(
    () => (leaveRequests || []).filter((r) => r.status === 'approved'),
    [leaveRequests]
  );

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const check = () => setIsMobile(mql.matches);
    check();
    mql.addEventListener('change', check);
    return () => mql.removeEventListener('change', check);
  }, []);

  useEffect(() => {
    loadLeaveRequests();
    loadLeaveTypes();
  }, [currentDate]);

  const loadLeaveRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/leaves/my-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeaveRequests(data.requests || []);
      }
    } catch (error) {
      console.error('❌ Erreur chargement demandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeaveTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/leaves/types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeaveTypes(data.leaveTypes || []);
      }
    } catch (error) {
      console.error('❌ Erreur chargement types:', error);
    }
  };

  // Générer les jours du mois
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay()); // Premier dimanche
    
    const days = [];
    const current = new Date(startDate);
    
    // 6 semaines × 7 jours = 42 jours
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  // Normalize to date-only (YYYY-MM-DD) to avoid timezone bugs: API dates are UTC midnight, calendar uses local days
  const toDateOnly = (d) => {
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  };

  // Obtenir les congés pour une date (tous statuts - vue mensuelle)
  const getLeavesForDate = (date) => {
    const dayStr = toDateOnly(date);
    return leaveRequests.filter(req => {
      const startStr = toDateOnly(req.start_date);
      const endStr = toDateOnly(req.end_date);
      return dayStr >= startStr && dayStr <= endStr;
    });
  };

  // Approved leaves for a date (annual view & exports)
  const getApprovedLeavesForDate = (date) => {
    const dayStr = toDateOnly(date);
    return approvedLeaves.filter(req => {
      const startStr = toDateOnly(req.start_date);
      const endStr = toDateOnly(req.end_date);
      return dayStr >= startStr && dayStr <= endStr;
    });
  };

  // Obtenir la couleur du type de congé
  const getLeaveTypeColor = (leaveTypeId) => {
    const type = leaveTypes.find(t => t.id === leaveTypeId);
    return type?.color || '#3498db';
  };

  const calendarDays = generateCalendarDays();
  const dayNames = [0, 1, 2, 3, 4, 5, 6].map(d =>
    new Date(2024, 0, 7 + d).toLocaleDateString(dateLocale, { weekday: 'short' })
  );

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Build flat list of approved leave days for export (one row per day, for the given year)
  const getApprovedLeaveDaysForExport = (year) => {
    const y = year != null ? year : (viewMode === 'annual' ? annualYear : currentDate.getFullYear());
    const rows = [];
    const typeName = (id) => leaveTypes.find(lt => lt.id === id)?.name || t('leaves.leave_type_fallback');
    approvedLeaves.forEach(leave => {
      const start = new Date(leave.start_date);
      const end = new Date(leave.end_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (d.getFullYear() !== y) continue;
        rows.push({
          date: d.toISOString().split('T')[0],
          dateFormatted: d.toLocaleDateString(dateLocale, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }),
          leaveType: typeName(leave.leave_type_id),
          daysRequested: leave.days_requested,
          halfDay: leave.half_day || false
        });
      }
    });
    rows.sort((a, b) => a.date.localeCompare(b.date));
    return rows;
  };

  const exportYear = viewMode === 'annual' ? annualYear : currentDate.getFullYear();

  const handleExportExcel = () => {
    const rows = getApprovedLeaveDaysForExport(exportYear);
    const headers = [t('leaves.start_date'), t('leaves.type'), t('leaves.days'), t('leaves.half_day')];
    const csvRows = [
      headers.join(','),
      ...rows.map(r => [
        `"${r.dateFormatted}"`,
        `"${r.leaveType}"`,
        r.daysRequested,
        r.halfDay ? 'Yes' : ''
      ].join(','))
    ];
    const blob = new Blob(['\ufeff' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave-calendar-approved-${exportYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const rows = getApprovedLeaveDaysForExport(exportYear);
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const title = `${t('leaves.calendar_title')} - ${t('leaves.approved')} (${exportYear})`;
      doc.setFontSize(14);
      doc.text(title, 14, 15);
      doc.setFontSize(10);
      let y = 25;
      const colWidths = [25, 50, 70, 25, 20];
      const headers = [t('leaves.start_date'), t('leaves.type'), 'Date (full)', t('leaves.days'), t('leaves.half_day')];
      doc.text(headers[0], 14, y);
      doc.text(headers[1], 14 + colWidths[0], y);
      doc.text(headers[2], 14 + colWidths[0] + colWidths[1], y);
      doc.text(headers[3], 14 + colWidths[0] + colWidths[1] + colWidths[2], y);
      doc.text(headers[4], 14 + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], y);
      y += 7;
      rows.slice(0, 80).forEach(r => {
        if (y > 270) return;
        doc.text(r.date, 14, y);
        doc.text(r.leaveType.substring(0, 22), 14 + colWidths[0], y);
        doc.text(r.dateFormatted.substring(0, 28), 14 + colWidths[0] + colWidths[1], y);
        doc.text(String(r.daysRequested), 14 + colWidths[0] + colWidths[1] + colWidths[2], y);
        doc.text(r.halfDay ? 'Yes' : '', 14 + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], y);
        y += 6;
      });
      if (rows.length > 80) {
        doc.setFontSize(9);
        doc.text(`... and ${rows.length - 80} more approved leave days.`, 14, y + 4);
      }
      doc.save(`leave-calendar-approved-${exportYear}.pdf`);
    } catch (e) {
      console.error('PDF export failed:', e);
      alert('PDF export failed. Ensure jspdf is installed (npm install jspdf).');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: 'white' }}>
        ⏳ {t('leaves.calendar_loading')}
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1400px',
      width: '100%',
      margin: '0 auto',
      padding: isMobile ? '12px' : '20px',
      background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
      minHeight: '100vh',
      color: '#ecf0f1',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      {/* En-tête */}
      <div style={{
        background: 'rgba(255,255,255,0.1)',
        borderRadius: isMobile ? '12px' : '15px',
        padding: isMobile ? '14px' : '25px',
        marginBottom: isMobile ? '12px' : '20px',
        backdropFilter: 'blur(10px)',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? '1.25rem' : '28px', fontWeight: 'bold', color: '#ecf0f1' }}>
            📅 {t('leaves.calendar_title')}
          </h2>
          
          <div style={{
            display: 'flex',
            gap: isMobile ? '8px' : '10px',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'space-between' : undefined
          }}>
            {!showExportButtons && (
              <button
                onClick={() => {
                  setShowExportButtons(true);
                  setViewMode('annual');
                }}
                style={{
                  padding: isMobile ? '8px 10px' : '10px 14px',
                  background: 'rgba(255,255,255,0.2)',
                  color: '#ecf0f1',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: isMobile ? '12px' : '13px'
                }}
              >
                📅 {t('leaves.generate_annual_calendar')}
              </button>
            )}
            {showExportButtons && (
              <>
                <button
                  onClick={handleExportPdf}
                  style={{
                    padding: isMobile ? '8px 10px' : '10px 14px',
                    background: 'rgba(231, 76, 60, 0.8)',
                    color: '#ecf0f1',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '13px'
                  }}
                >
                  📄 {t('leaves.export_to_pdf')}
                </button>
                <button
                  onClick={handleExportExcel}
                  style={{
                    padding: isMobile ? '8px 10px' : '10px 14px',
                    background: 'rgba(39, 174, 96, 0.9)',
                    color: '#ecf0f1',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '13px'
                  }}
                >
                  📊 {t('leaves.export_to_excel')}
                </button>
              </>
            )}
            {viewMode === 'month' && (
              <>
                <button
                  onClick={() => navigateMonth(-1)}
                  style={{
                    padding: isMobile ? '8px 10px' : '10px 16px',
                    background: 'rgba(255,255,255,0.2)',
                    color: '#ecf0f1',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '13px' : undefined
                  }}
                >
                  ← {t('leaves.calendar_previous')}
                </button>
                <button
                  onClick={goToToday}
                  style={{
                    padding: isMobile ? '8px 12px' : '10px 20px',
                    background: '#3498db',
                    color: '#ecf0f1',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '13px' : undefined
                  }}
                >
                  {t('leaves.calendar_today')}
                </button>
                <h3 style={{
                  margin: 0,
                  fontSize: isMobile ? '1rem' : '22px',
                  fontWeight: 'bold',
                  minWidth: isMobile ? 0 : '200px',
                  textAlign: 'center',
                  flex: isMobile ? '1 1 100%' : undefined
                }}>
                  {currentDate.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}
                </h3>
                <button
                  onClick={() => navigateMonth(1)}
                  style={{
                    padding: isMobile ? '8px 10px' : '10px 16px',
                    background: 'rgba(255,255,255,0.2)',
                    color: '#ecf0f1',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '13px' : undefined
                  }}
                >
                  {t('leaves.calendar_next')} →
                </button>
              </>
            )}
            {viewMode === 'annual' && (
              <>
                <button
                  onClick={() => setAnnualYear(annualYear - 1)}
                  style={{
                    padding: isMobile ? '8px 10px' : '10px 16px',
                    background: 'rgba(255,255,255,0.2)',
                    color: '#ecf0f1',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  ← {annualYear - 1}
                </button>
                <h3 style={{ margin: 0, fontSize: isMobile ? '1rem' : '22px', fontWeight: 'bold', minWidth: '120px', textAlign: 'center' }}>
                  {annualYear}
                </h3>
                <button
                  onClick={() => setAnnualYear(annualYear + 1)}
                  style={{
                    padding: isMobile ? '8px 10px' : '10px 16px',
                    background: 'rgba(255,255,255,0.2)',
                    color: '#ecf0f1',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {annualYear + 1} →
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div style={{
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '10px',
        padding: isMobile ? '12px' : '15px',
        marginBottom: isMobile ? '12px' : '20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: isMobile ? '10px' : '15px',
        alignItems: 'center',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <span style={{ fontWeight: 'bold', fontSize: isMobile ? '13px' : undefined }}>{t('leaves.calendar_legend')}</span>
        {leaveTypes.map(type => (
          <div key={type.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '4px',
              background: type.color,
              flexShrink: 0
            }}></div>
            <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{type.name}</span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: isMobile ? 0 : 'auto' }}>
          <div style={{
            width: '18px',
            height: '18px',
            borderRadius: '4px',
            background: 'rgba(243, 156, 18, 0.3)',
            border: '2px solid #f39c12',
            flexShrink: 0
          }}></div>
          <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{t('leaves.half_day')}</span>
        </div>
      </div>

      {/* Annual view: 12 months grid with approved leaves only */}
      {viewMode === 'annual' && (
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '15px',
          padding: isMobile ? '10px' : '20px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          width: '100%',
          boxSizing: 'border-box',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? '12px' : '16px' }}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(monthIndex => {
              const monthDate = new Date(annualYear, monthIndex, 1);
              const firstDay = new Date(annualYear, monthIndex, 1);
              const startDate = new Date(firstDay);
              startDate.setDate(startDate.getDate() - startDate.getDay());
              const days = [];
              const current = new Date(startDate);
              for (let i = 0; i < 42; i++) {
                days.push(new Date(current));
                current.setDate(current.getDate() + 1);
              }
              return (
                <div key={monthIndex} style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '8px',
                  background: '#fff'
                }}>
                  <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '6px', fontSize: isMobile ? '12px' : '14px' }}>
                    {monthDate.toLocaleDateString(dateLocale, { month: 'short', year: '2-digit' })}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', fontSize: '10px' }}>
                    {dayNames.map(d => (
                      <div key={d} style={{ textAlign: 'center', fontWeight: '600', color: '#7f8c8d' }}>{d.charAt(0)}</div>
                    ))}
                    {days.map((day, idx) => {
                      const isCurrentMonth = day.getMonth() === monthIndex;
                      const dayLeaves = getApprovedLeavesForDate(day);
                      return (
                        <div
                          key={idx}
                          style={{
                            minHeight: '18px',
                            padding: '2px',
                            border: '1px solid transparent',
                            borderRadius: '3px',
                            background: isCurrentMonth ? (dayLeaves.length ? '#e8f5e9' : '#fff') : '#f5f5f5',
                            opacity: isCurrentMonth ? 1 : 0.5,
                            fontSize: '9px',
                            color: isCurrentMonth ? '#2c3e50' : '#95a5a6'
                          }}
                          title={dayLeaves.length ? dayLeaves.map(l => leaveTypes.find(t => t.id === l.leave_type_id)?.name).join(', ') : ''}
                        >
                          {day.getDate()}
                          {dayLeaves.length > 0 && <span style={{ color: '#27ae60', fontWeight: 'bold' }}> •</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Calendrier mois — on mobile: horizontal scroll so day names and leave names stay readable */}
      {viewMode === 'month' && (
      <div style={{
        background: 'rgba(255,255,255,0.95)',
        borderRadius: isMobile ? '12px' : '15px',
        padding: isMobile ? '10px' : '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: isMobile ? 'auto' : 'visible',
        overflowY: 'visible',
        WebkitOverflowScrolling: 'touch'
      }}>
        <div style={{
          minWidth: isMobile ? 560 : undefined,
          width: isMobile ? 'max-content' : '100%'
        }}>
        {/* En-têtes des jours */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(7, minmax(76px, 1fr))' : 'repeat(7, minmax(0, 1fr))',
          gap: isMobile ? '6px' : '8px',
          marginBottom: isMobile ? '6px' : '10px'
        }}>
          {dayNames.map(day => (
            <div key={day} style={{
              padding: isMobile ? '8px 6px' : '12px',
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#2c3e50',
              fontSize: isMobile ? '13px' : '14px',
              background: '#ecf0f1',
              borderRadius: isMobile ? '4px' : '8px'
            }}>
              {day}
            </div>
          ))}
        </div>

        {/* Grille du calendrier */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(7, minmax(76px, 1fr))' : 'repeat(7, minmax(0, 1fr))',
          gap: isMobile ? '6px' : '8px'
        }}>
          {calendarDays.map((day, index) => {
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            const isToday = day.toDateString() === new Date().toDateString();
            const dayLeaves = getLeavesForDate(day);
            const dateStr = day.toISOString().split('T')[0];
            
            return (
              <div
                key={index}
                onClick={() => setSelectedDate(day)}
                style={{
                  minHeight: isMobile ? '82px' : '100px',
                  padding: isMobile ? '6px' : '8px',
                  border: `2px solid ${isToday ? '#3498db' : isCurrentMonth ? '#e0e0e0' : '#f5f5f5'}`,
                  borderRadius: '8px',
                  background: isCurrentMonth ? '#ffffff' : '#f9f9f9',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative',
                  opacity: isCurrentMonth ? 1 : 0.5
                }}
                onMouseEnter={(e) => {
                  if (isCurrentMonth) {
                    e.currentTarget.style.background = '#f0f8ff';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isCurrentMonth ? '#ffffff' : '#f9f9f9';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {/* Numéro du jour */}
                <div style={{
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: isToday ? 'bold' : 'normal',
                  color: isToday ? '#3498db' : isCurrentMonth ? '#2c3e50' : '#95a5a6',
                  marginBottom: isMobile ? '2px' : '5px',
                  textAlign: 'right'
                }}>
                  {day.getDate()}
                  {isToday && ' 📍'}
                </div>

                {/* Congés du jour */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {dayLeaves.map(leave => {
                    const isHalfDay = leave.half_day;
                    const typeColor = getLeaveTypeColor(leave.leave_type_id);
                    const leaveType = leaveTypes.find(t => t.id === leave.leave_type_id);
                    
                    return (
                      <div
                        key={leave.id}
                        style={{
                          background: isHalfDay ? `${typeColor}33` : typeColor,
                          border: `1px solid ${typeColor}`,
                          borderRadius: '4px',
                          padding: isMobile ? '4px 6px' : '4px 6px',
                          fontSize: isMobile ? '11px' : '11px',
                          color: isHalfDay ? '#2c3e50' : '#ffffff',
                          fontWeight: '500',
                          overflow: 'hidden',
                          position: 'relative',
                          ...(isMobile ? { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 } : { textOverflow: 'ellipsis', whiteSpace: 'nowrap' })
                        }}
                        title={`${leaveType?.name || t('leaves.leave_type_fallback')}: ${leave.start_date} - ${leave.end_date} (${leave.days_requested} ${t('leaves.days')})`}
                      >
                        {isHalfDay && '🕐 '}
                        {leaveType?.name || t('leaves.leave_type_fallback')}
                        {leave.request_priority === 'urgent' && ' 🚨'}
                      </div>
                    );
                  })}
                </div>

                {/* Indicateur si plus de congés */}
                {dayLeaves.length > 3 && (
                  <div style={{
                    position: 'absolute',
                    bottom: '4px',
                    right: '4px',
                    fontSize: '10px',
                    color: '#7f8c8d',
                    fontWeight: 'bold'
                  }}>
                    +{dayLeaves.length - 3}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </div>
      )}

      {/* Modal détails date */}
      {selectedDate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={() => setSelectedDate(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '15px',
              padding: '25px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>
                📅 {selectedDate.toLocaleDateString(dateLocale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </h3>
              <button
                onClick={() => setSelectedDate(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#7f8c8d'
                }}
              >
                ×
              </button>
            </div>

            {getLeavesForDate(selectedDate).length === 0 ? (
              <p style={{ color: '#7f8c8d', textAlign: 'center' }}>
                {t('leaves.no_leave_this_day')}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {getLeavesForDate(selectedDate).map(leave => {
                  const leaveType = leaveTypes.find(t => t.id === leave.leave_type_id);
                  const statusColors = {
                    pending: '#f39c12',
                    approved: '#27ae60',
                    rejected: '#e74c3c',
                    cancelled: '#95a5a6'
                  };
                  
                  return (
                    <div
                      key={leave.id}
                      style={{
                        border: `2px solid ${leaveType?.color || '#3498db'}`,
                        borderRadius: '10px',
                        padding: '15px',
                        background: `${leaveType?.color || '#3498db'}10`
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                        <div>
                          <h4 style={{ margin: '0 0 5px 0', color: '#2c3e50' }}>
                            {leaveType?.name || t('leaves.leave_type_fallback')}
                          </h4>
                          <p style={{ margin: '0', color: '#7f8c8d', fontSize: '14px' }}>
                            {new Date(leave.start_date).toLocaleDateString(dateLocale)} - {new Date(leave.end_date).toLocaleDateString(dateLocale)}
                            {leave.half_day && ` (${leave.half_day_period === 'AM' ? t('leaves.half_day_am') : t('leaves.half_day_pm')})`}
                          </p>
                          <p style={{ margin: '5px 0 0 0', color: '#7f8c8d', fontSize: '13px' }}>
                            {leave.days_requested} {t('leaves.days')} • {t('leaves.priority')}: {leave.request_priority === 'urgent' ? t('leaves.priority_urgent') : t('leaves.priority_normal')}
                          </p>
                        </div>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: statusColors[leave.status] || '#95a5a6',
                          color: 'white'
                        }}>
                          {leave.status === 'pending' ? t('leaves.pending') :
                           leave.status === 'approved' ? t('leaves.approved') :
                           leave.status === 'rejected' ? t('leaves.rejected') :
                           t('leaves.cancelled')}
                        </span>
                      </div>
                      
                      {leave.reason && (
                        <p style={{ margin: '10px 0 0 0', color: '#2c3e50', fontSize: '14px' }}>
                          <strong>{t('leaves.reason')}:</strong> {leave.reason}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveCalendar;

